/**
 * 내 품목(개인 즐겨찾기) API + 경량 공유 스토어.
 * 식별은 서버 세션(group_id+user_id) — 프론트는 JWT(Bearer)만 전달.
 * 별 토글과 "내 품목" 목록이 같은 코드 Set 을 공유해 즉시 동기화된다.
 */
import { useEffect, useState } from "react";

function token() {
  return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
}
export function isLoggedIn() {
  return !!token();
}
function authHeaders(json) {
  const h = {};
  const t = token();
  if (t) h.Authorization = `Bearer ${t}`;
  if (json) h["Content-Type"] = "application/json";
  return h;
}

export async function fetchWatchlist() {
  if (!isLoggedIn()) return [];
  try {
    const r = await fetch("/api/onto/watchlist", { headers: authHeaders() });
    if (!r.ok) return [];
    const d = await r.json();
    return d.items || [];
  } catch {
    return [];
  }
}
async function apiAdd(code, name) {
  return fetch("/api/onto/watchlist", {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify({ code, name }),
  }).then((r) => r.ok);
}
async function apiRemove(code) {
  return fetch(`/api/onto/watchlist/${encodeURIComponent(code)}`, {
    method: "DELETE",
    headers: authHeaders(),
  }).then((r) => r.ok);
}

// ── 공유 스토어 (코드 Set + 변동 건수) ──
let _codes = null; // Set<string> | null(미로드)
let _changeCount = 0;
const _subs = new Set();
const _emit = () => _subs.forEach((fn) => fn());

export async function refreshChangeCount() {
  if (!isLoggedIn()) {
    _changeCount = 0;
    _emit();
    return 0;
  }
  try {
    const r = await fetch("/api/onto/watchlist/changes-count", {
      headers: authHeaders(),
    });
    if (r.ok) {
      const d = await r.json();
      _changeCount = d.count || 0;
    }
  } catch {
    /* keep */
  }
  _emit();
  return _changeCount;
}
export function getChangeCount() {
  return _changeCount;
}
export async function markSeen() {
  if (!isLoggedIn()) return;
  try {
    await fetch("/api/onto/watchlist/seen", {
      method: "POST",
      headers: authHeaders(),
    });
  } catch {
    /* ignore */
  }
  _changeCount = 0;
  _emit();
}

export async function ensureLoaded() {
  if (_codes) return _codes;
  _codes = new Set();
  const items = await fetchWatchlist();
  items.forEach((i) => _codes.add(i.code));
  _emit();
  refreshChangeCount(); // 배지용(비동기)
  return _codes;
}
export function getCodes() {
  return _codes || new Set();
}
export async function toggleWatch(code, name) {
  await ensureLoaded();
  const has = _codes.has(code);
  // 낙관적 갱신 후 API (실패 시 롤백)
  if (has) _codes.delete(code);
  else _codes.add(code);
  _emit();
  const ok = has ? await apiRemove(code) : await apiAdd(code, name);
  if (!ok) {
    if (has) _codes.add(code);
    else _codes.delete(code);
    _emit();
  }
  return !has;
}
export function invalidate() {
  _codes = null;
}

// ── React 훅 ──
export function useWatchlist() {
  const [, force] = useState(0);
  useEffect(() => {
    ensureLoaded();
    const unsub = () => force((x) => x + 1);
    _subs.add(unsub);
    return () => _subs.delete(unsub);
  }, []);
  return {
    loggedIn: isLoggedIn(),
    has: (c) => getCodes().has(c),
    count: getCodes().size,
    changeCount: getChangeCount(),
    toggle: toggleWatch,
  };
}
